import { roundMoney } from "@/domain/money/round-money";
import type { CartLineCapture } from "@/domain/cart/cart-validity.types";
import type { CartLine, CartLineValidityCapture, CartModifierGroupStored } from "@/shared/cart/model.types";

export type BuildCartLineValidityCaptureInput = {
  item_price: number;
  menu_version: number;
  catalog_fingerprint: string;
  selected_modifiers: CartModifierGroupStored[];
};

/** Collects sorted modifier_group_item_id values from a cart line. */
export function collectModifierIdsFromLine(line: Pick<CartLine, "selected_modifiers">): string[] {
  const ids: string[] = [];
  for (const group of line.selected_modifiers ?? []) {
    for (const mod of group.items ?? []) {
      const id = String(mod.modifier_group_item_id ?? "").trim();
      if (id) ids.push(id);
    }
  }
  return ids.sort();
}

/** Builds persisted capture metadata for a newly added cart line. */
export function buildCartLineValidityCapture(
  input: BuildCartLineValidityCaptureInput
): CartLineValidityCapture {
  return {
    capturedUnitPrice: roundMoney(input.item_price),
    capturedMenuVersion: Math.max(0, Math.floor(Number(input.menu_version) || 0)),
    capturedCatalogFingerprint: input.catalog_fingerprint,
    capturedModifierIds: collectModifierIdsFromGroups(input.selected_modifiers),
  };
}

export function collectModifierIdsFromGroups(
  groups: CartModifierGroupStored[] | undefined | null
): string[] {
  const ids: string[] = [];
  for (const group of groups ?? []) {
    for (const mod of group.items ?? []) {
      const id = String(mod.modifier_group_item_id ?? "").trim();
      if (id) ids.push(id);
    }
  }
  return ids.sort();
}

/** Maps a cart line to validity resolver capture input. */
export function cartLineToCapture(line: CartLine): CartLineCapture | null {
  if (!line.validityCapture) return null;
  return {
    client_item_id: line.client_item_id,
    capturedUnitPrice: line.validityCapture.capturedUnitPrice,
    capturedMenuVersion: line.validityCapture.capturedMenuVersion,
    capturedCatalogFingerprint: line.validityCapture.capturedCatalogFingerprint,
    capturedModifierIds: [...line.validityCapture.capturedModifierIds],
  };
}

/** Maps all cart lines that have capture metadata. */
export function cartLinesToCaptures(lines: ReadonlyArray<CartLine>): CartLineCapture[] {
  return lines
    .map(cartLineToCapture)
    .filter((capture): capture is CartLineCapture => capture != null);
}
